import { motion } from "framer-motion";
import {
  FiArrowRight,
  FiGlobe,
  FiMessageSquare,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { stats } from "../data/fakeData";

const AboutPage = () => {
  const team = [
    {
      name: "Alex Johnson",
      role: "Founder & CEO",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face",
    },
    {
      name: "Sarah Williams",
      role: "Head of Curation",
      avatar:
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&crop=face",
    },
    {
      name: "Mike Chen",
      role: "Lead Developer",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face",
    },
    {
      name: "Emma Davis",
      role: "Community Manager",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face",
    },
  ];

  const values = [
    {
      icon: FiShield,
      title: "Quality First",
      desc: "Every website is manually reviewed before being listed. We maintain high standards so you can trust every recommendation.",
    },
    {
      icon: FiUsers,
      title: "Community Driven",
      desc: "Real reviews from real users. Our community of 28,000+ members ensures honest, unbiased ratings.",
    },
    {
      icon: FiGlobe,
      title: "Comprehensive",
      desc: "With 1,847+ websites across 12 categories, we cover every corner of the internet worth exploring.",
    },
    {
      icon: FiMessageSquare,
      title: "Transparent",
      desc: "No sponsored placements. No pay-to-rank. Rankings are purely based on community ratings and reviews.",
    },
  ];

  return (
    <div className="section-tinted min-h-screen">
      {/* Hero */}
      <section className="section-white">
        <div className="container-main py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="accent-line" />
            <p className="label-tag mb-2">About Us</p>
            <h1 className="page-title mb-6">
              We help people discover
              <span className="text-indigo-600"> the best of the web.</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              SiteVault was founded in 2022 with a simple mission: cut through
              the noise and help people find genuinely great websites. We're a
              team of web enthusiasts who believe that quality curation matters
              more than ever.
            </p>
            <p className="text-base text-slate-500 leading-relaxed mb-8">
              Every day we review hundreds of website submissions, maintain our
              quality standards, and work to build a community where honest
              recommendations thrive. Our directory isn't just a list — it's a
              trusted guide built by people who love the web.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/websites" className="btn-primary">
                Browse Directory <FiArrowRight size={14} />
              </Link>
              <Link to="/submit" className="btn-secondary">
                Submit a Website
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="container-main py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            {
              value: stats.totalWebsites.toLocaleString() + "+",
              label: "Websites Listed",
              icon: FiGlobe,
              color: "bg-indigo-50 text-indigo-600",
            },
            {
              value: stats.totalCategories,
              label: "Categories",
              icon: FiGlobe,
              color: "bg-violet-50 text-violet-600",
            },
            {
              value: stats.totalReviews.toLocaleString() + "+",
              label: "Community Reviews",
              icon: FiMessageSquare,
              color: "bg-emerald-50 text-emerald-600",
            },
            {
              value: stats.totalUsers.toLocaleString() + "+",
              label: "Active Members",
              icon: FiUsers,
              color: "bg-amber-50 text-amber-600",
            },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="card p-6 text-center"
            >
              <div
                className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center mx-auto mb-3`}
              >
                <stat.icon size={20} />
              </div>
              <p className="text-3xl font-extrabold text-slate-900 font-display">
                {stat.value}
              </p>
              <p className="text-sm font-semibold text-slate-500 mt-1">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="section-white">
        <div className="container-main py-16 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="accent-line mx-auto" />
            <p className="label-tag mb-2 text-center">What We Stand For</p>
            <h2 className="section-title">Our Values</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((val, i) => (
              <motion.div
                key={val.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card p-6"
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center mb-4">
                  <val.icon size={18} className="text-indigo-600" />
                </div>
                <h3 className="text-base font-extrabold text-slate-900 mb-2">
                  {val.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {val.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="container-main py-16 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="accent-line mx-auto" />
          <p className="label-tag mb-2 text-center">The People</p>
          <h2 className="section-title">Meet the Team</h2>
          <p className="section-subtitle mx-auto text-center max-w-xl">
            A small team of web enthusiasts passionate about helping people find
            the best digital tools.
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="card p-6 text-center"
            >
              <img
                src={member.avatar}
                alt={member.name}
                className="w-20 h-20 rounded-2xl object-cover mx-auto mb-4 ring-4 ring-slate-100"
              />
              <h3 className="font-extrabold text-slate-900">{member.name}</h3>
              <p className="text-sm font-semibold text-slate-500 mt-1">
                {member.role}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-main pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="card p-10 lg:p-14 text-center bg-gradient-to-br from-indigo-600 to-violet-700 border-0"
        >
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
            Join our community
          </h2>
          <p className="text-indigo-200 mb-6 max-w-lg mx-auto">
            Create an account to submit websites, write reviews, and help others
            discover quality resources.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/submit"
              className="inline-flex items-center gap-2 bg-white text-indigo-700 font-extrabold px-6 py-3 rounded-xl hover:bg-indigo-50 transition-all shadow-lg"
            >
              Get Started Free <FiArrowRight size={16} />
            </Link>
            <Link
              to="/websites"
              className="text-indigo-200 hover:text-white font-semibold flex items-center gap-1.5 transition-colors"
            >
              Browse Directory <FiArrowRight size={14} />
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default AboutPage;
